import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import styles from 'components/medicalHistory/page.module.scss';
import Stack from '@mui/material/Stack';
import Button from 'components/common/button';
import { StateTypeLocalStorage, usePersistMHReducer } from 'reducer/medicalHistory';
import MetaTag from 'components/meta';
import { useSelector } from 'react-redux';
import { NullablePatientDTO } from 'types/patients';
import { RootState } from 'store/store';
import uniqBy from 'lodash/uniqBy';
import { reviewOfSystemMappingData } from 'components/medicalHistory/PageCommonIssues';
import clsx from 'clsx';

const questionOptions = [
  'None',
  'Constitutional (general body)',
  'Joints, Muscles, and Bones',
  'Dermatology (skin)',
  'Cardiovascular (heart & blood vessels)',
  'Neurological (nerves & brain)',
  'Hematology (blood)',
  'Genitourinary (kidney/bladder & genitals)',
  'ENT (ears, nose, & throat)',
  'Respiratory (lungs & breathing)',
  'Gastrointestinal (stomach & intestines)',
];

type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  multiple?: boolean;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function AnyRecentIssues({
  nextPage,
  nextOrSubmitButton,
  multiple = true,
  actionPage,
}: Props) {
  const patient = useSelector(
    (state: RootState) => state.patient,
  ) as NullablePatientDTO;

  const [selectedOption, setSelectedOption] = useState<string[]>(['None']);
  const [error, setError] = useState(false);
  const [state, dispatch] = usePersistMHReducer();

  const findNoneFromSelected = () => selectedOption.find((e) => e === questionOptions[0]);

  const clickNextOrSubmit = async (nextPage: number) => {
    if (selectedOption.length === 0) {
      setError(true);
      return;
    }
    await dispatch({
      type: actionPage,
      name: 'Any recent issues with these other bodily systems?',
      value: selectedOption,
    });
    nextOrSubmitButton(nextPage, !!findNoneFromSelected()); // true for submit from this page
  };

  const onSelectOptions = useCallback((item: string) => {
    if (multiple) {
      if (item === 'None') {
        setSelectedOption([item]);
      } else {
        setSelectedOption((old) => {
          if (old.toString() === 'None') {
            return [item];
          } if (old.find((value) => value === item)) {
            return old.filter((data) => data !== item);
          } return [...old, item];
        });
      }
    } else {
      setSelectedOption([item]);
    }

    setError(false);
  }, []);

  useEffect(() => {
    if (state) {
      const recentIssues = state.find(
        (p: StateTypeLocalStorage) => p.name === actionPage,
      );
      if (recentIssues.value.length) {
        setSelectedOption(recentIssues.value);
      } else {
        const selectedIssues = uniqBy(
          patient?.reviewOfSystem?.reviewOfSystemElements?.filter((item) => item.title !== 'Patient Denies'),
          'discriminator',
        ).map((data) => data.discriminator) as string[];
        const mappingIssues = selectedIssues.map((item) => reviewOfSystemMappingData.find(
          (data) => data.anchor === item,
        )?.name) as string[];
        if (mappingIssues?.length) {
          setSelectedOption(mappingIssues);
        } else {
          setSelectedOption(['None']);
        }
      }
    }
  }, []);

  return (
    <>
      <MetaTag title="Any recent issues with these other bodily systems?" />
      <Box pb={3.5} mb={4}>
        <Box>
          <h2 className={`marginTopZero ${styles.titleText}`}>
            Any recent issues with these other bodily systems?
          </h2>
          <p className={styles.titleTextSubText}>
            Select each that has been problematic in the last few months
          </p>
          {error && (
            <p className="error-message">
              Please select at least one option.
            </p>
          )}
          <Stack>
            {questionOptions.map((option) => {
              const selectedClass = selectedOption.find(
                (item) => item === option,
              );
              return (
                <p
                  key={option}
                  className={clsx('option', { 'selected-option': selectedClass }, 'cursor-pointer')}
                  onKeyUp={() => {}}
                  onClick={() => onSelectOptions(option)}
                >
                  {option}
                </p>
              );
            })}
          </Stack>
        </Box>
        <div className="next-button-fixed">
          <Button onClick={() => clickNextOrSubmit(nextPage)}>
            {findNoneFromSelected() ? 'Submit' : 'Next'}
          </Button>
        </div>
      </Box>
    </>
  );
}

export default AnyRecentIssues;
